import { apiClient, setAuthRefreshHandler, setAuthTokenProvider } from './api/client';

// Vite injects env vars via import.meta.env in the browser build. In Node (playground scripts),
// import.meta.env is undefined, so we also honor process.env.
const USE_MOCK_AUTH = String(
  import.meta?.env?.VITE_USE_MOCK_AUTH
    ?? (typeof process !== 'undefined' ? process.env?.VITE_USE_MOCK_AUTH : undefined)
    ?? 'false',
).toLowerCase() === 'true';
const STORAGE_KEY = 'nbna_auth_session_v1';
const LEGACY_STORAGE_KEY = STORAGE_KEY;

let mockAuthPromise;
let mockAuthModule;

const loadMockAuth = async () => {
  if (mockAuthModule) {
    return mockAuthModule;
  }
  if (!mockAuthPromise) {
    mockAuthPromise = import('./mocks/mockAuth.js')
      .then((module) => {
        mockAuthModule = module;
        return module;
      })
      .catch((error) => {
        console.error('[auth] failed to load mock auth module:', error);
        throw error;
      });
  }
  return mockAuthPromise;
};

const subscribers = new Set();
const sessionState = { current: null };

function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    // A QR scan commonly opens a new browser tab. localStorage is shared by
    // same-origin tabs, while sessionStorage would make that scan appear signed
    // out even when the member just signed in elsewhere in this browser.
    return window.localStorage ?? window.sessionStorage ?? null;
  } catch (error) {
    try {
      return window.sessionStorage ?? null;
    } catch (nested) {
      console.error('[auth] storage unavailable:', error);
      return null;
    }
  }
}

function migrateLegacyStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const persistentStorage = window.localStorage ?? null;
    const legacyStorage = window.sessionStorage ?? null;
    if (!persistentStorage || !legacyStorage) return null;
    const legacyRaw = legacyStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacyRaw) return null;
    persistentStorage.setItem(STORAGE_KEY, legacyRaw);
    legacyStorage.removeItem(LEGACY_STORAGE_KEY);
    return legacyRaw;
  } catch (error) {
    console.error('[auth] failed to migrate legacy storage:', error);
    return null;
  }
}

function readStoredSession() {
  const storage = getStorage();
  if (!storage) return null;
  try {
    let raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      raw = migrateLegacyStorage();
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (error) {
    console.error('[auth] failed to read stored session:', error);
    return null;
  }
}

function writeStoredSession(session) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(session));
    if (typeof window !== 'undefined') {
      try {
        const tabStorage = window.sessionStorage ?? null;
        if (tabStorage && storage !== tabStorage) tabStorage.removeItem(STORAGE_KEY);
      } catch {
        // The persistent write succeeded; unavailable tab storage needs no cleanup.
      }
    }
  } catch (error) {
    console.error('[auth] failed to persist session:', error);
  }
}

function clearStoredSession() {
  if (typeof window === 'undefined') return;
  for (const name of ['localStorage', 'sessionStorage']) {
    try {
      const storage = window[name];
      storage?.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('[auth] failed to clear session:', error);
    }
  }
}

function notifySubscribers(event, session) {
  subscribers.forEach((callback) => callback(event, session));
}

export function setSession(session, event) {
  sessionState.current = session;
  if (session) {
    writeStoredSession(session);
  } else {
    clearStoredSession();
  }
  notifySubscribers(event, session);
}

export function normalizeBackendUser(user) {
  if (!user) return null;
  const backendRole = user.role;
  const uiRole = user.is_admin === true
    ? 'admin'
    : backendRole === 'Executive'
      ? 'member'
      : backendRole === 'Member'
        ? 'member'
        : backendRole
          ? String(backendRole).toLowerCase()
          : 'member';

  return {
    ...user,
    role: uiRole,
    uiRole,
    backendRole,
  };
}

function getAccessToken() {
  return sessionState.current?.accessToken
    || sessionState.current?.token
    || null;
}

function getRefreshToken() {
  return sessionState.current?.refreshToken || null;
}

async function refreshSession() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.warn('[auth] refresh requested without refresh token');
    return null;
  }

  try {
    const data = await apiClient.post(
      '/auth/refresh',
      { refresh_token: refreshToken },
      { skipAuth: true, skipAuthRefresh: true }
    );

    const accessToken = data?.access_token || data?.token;
    const nextRefreshToken = data?.refresh_token || refreshToken;
    const tokenType = data?.token_type || sessionState.current?.tokenType || 'bearer';

    if (!accessToken) {
      throw new Error('Refresh response missing access_token');
    }

    const nextSession = {
      ...sessionState.current,
      accessToken,
      token: accessToken,
      refreshToken: nextRefreshToken,
      tokenType,
    };

    setSession(nextSession, 'TOKEN_REFRESHED');
    return accessToken;
  } catch (error) {
    console.error('[auth] token refresh failed:', error);
    setSession(null, 'SIGNED_OUT');
    return null;
  }
}

async function signInReal({ email, password }) {
  if (!email || !password) {
    throw new Error('Email and password are required');
  }

  const data = await apiClient.post(
    '/auth/login',
    { email, password },
    { skipAuth: true, skipAuthRefresh: true }
  );

  const accessToken = data?.token || data?.access_token;
  const refreshToken = data?.refresh_token;
  const tokenType = data?.token_type || 'bearer';

  if (!accessToken || !refreshToken) {
    console.error('[auth] login response missing tokens:', data);
    throw new Error('Login failed: missing token data');
  }

  let normalizedUser = normalizeBackendUser(data?.user);
  if (!normalizedUser) {
    try {
      const sessionResponse = await apiClient.get('/auth/session', {
        authToken: accessToken,
        skipAuthRefresh: true,
      });
      normalizedUser = normalizeBackendUser(sessionResponse?.user);
    } catch (error) {
      console.error('[auth] failed to hydrate user after login:', error);
    }
  }

  if (!normalizedUser) {
    throw new Error('Login failed: missing user data');
  }

  const session = {
    user: normalizedUser,
    accessToken,
    token: accessToken,
    refreshToken,
    tokenType,
  };

  setSession(session, 'SIGNED_IN');
  return session;
}

async function signOutReal() {
  const accessToken = getAccessToken();

  if (accessToken) {
    try {
      await apiClient.post('/auth/logout', undefined, {
        authToken: accessToken,
        skipAuthRefresh: true,
      });
    } catch (error) {
      console.error('[auth] logout failed:', error);
    }
  }

  setSession(null, 'SIGNED_OUT');
}

async function resetPasswordReal(email) {
  if (!email) {
    throw new Error('Email is required');
  }

  await apiClient.post('/auth/reset-password', { email }, { skipAuth: true, skipAuthRefresh: true });
  return true;
}

async function acceptInviteReal(token, password) {
  if (!token) throw new Error('Token is required');
  if (!password) throw new Error('Password is required');

  await apiClient.post(
    '/auth/accept-invite',
    { token, password },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return true;
}

async function confirmResetPasswordReal(token, password) {
  if (!token) throw new Error('Token is required');
  if (!password) throw new Error('Password is required');

  await apiClient.post(
    '/auth/confirm-reset-password',
    { token, password },
    { skipAuth: true, skipAuthRefresh: true }
  );
  return true;
}

async function getSessionReal() {
  if (!sessionState.current) {
    sessionState.current = readStoredSession();
  }

  if (!sessionState.current) return null;

  const accessToken = getAccessToken();
  if (!accessToken) {
    const refreshed = await refreshSession();
    if (!refreshed) return null;
  }

  try {
    const data = await apiClient.get('/auth/session');
    const normalizedUser = normalizeBackendUser(data?.user);

    if (!normalizedUser) {
      throw new Error('Session response missing user data');
    }

    const nextSession = {
      ...sessionState.current,
      user: normalizedUser,
    };

    setSession(nextSession, 'SESSION_UPDATED');
    return nextSession;
  } catch (error) {
    console.error('[auth] failed to fetch session:', error);

    if (error?.status === 401) {
      setSession(null, 'SIGNED_OUT');
      return null;
    }

    // Preserve the cached session for transient backend/session endpoint failures.
    // Other authenticated requests still have a chance to refresh and succeed.
    return sessionState.current;
  }
}

function onAuthStateChangeReal(callback) {
  subscribers.add(callback);
  callback('INITIAL', sessionState.current);

  return {
    data: {
      subscription: {
        unsubscribe: () => {
          subscribers.delete(callback);
        },
      },
    },
  };
}

if (!USE_MOCK_AUTH) {
  sessionState.current = readStoredSession();
  setAuthTokenProvider(() => getAccessToken());
  setAuthRefreshHandler(() => refreshSession());
}

export const signIn = async (...args) => {
  if (USE_MOCK_AUTH) {
    const mockAuth = await loadMockAuth();
    return mockAuth.signIn(...args);
  }
  return signInReal(...args);
};

export const signOut = async (...args) => {
  if (USE_MOCK_AUTH) {
    const mockAuth = await loadMockAuth();
    return mockAuth.signOut(...args);
  }
  return signOutReal(...args);
};

export const resetPassword = async (...args) => {
  if (USE_MOCK_AUTH) {
    const mockAuth = await loadMockAuth();
    return mockAuth.resetPassword(...args);
  }
  return resetPasswordReal(...args);
};

export const acceptInvite = async (...args) => {
  return acceptInviteReal(...args);
};

export const confirmResetPassword = async (...args) => {
  return confirmResetPasswordReal(...args);
};

export const getSession = async (...args) => {
  if (USE_MOCK_AUTH) {
    const mockAuth = await loadMockAuth();
    return mockAuth.getSession(...args);
  }
  return getSessionReal(...args);
};

export function onAuthStateChange(callback) {
  if (!USE_MOCK_AUTH) {
    return onAuthStateChangeReal(callback);
  }

  if (mockAuthModule?.onAuthStateChange) {
    return mockAuthModule.onAuthStateChange(callback);
  }

  let activeSubscription = null;
  let unsubscribed = false;

  const subscription = {
    unsubscribe: () => {
      unsubscribed = true;
      activeSubscription?.data?.subscription?.unsubscribe?.();
    },
  };

  loadMockAuth()
    .then((mockAuth) => {
      if (unsubscribed) return;
      activeSubscription = mockAuth.onAuthStateChange(callback);
    })
    .catch((error) => {
      console.error('[auth] failed to attach mock auth subscription:', error);
    });

  return { data: { subscription } };
}

// Helper function to check if a user has admin access
export function isAdmin(session) {
  if (!session?.user) return false;
  return session.user.role?.toLowerCase() === 'admin' || session.user.is_admin === true;
}

// Helper function to check if a user has executive access
export function isExecutive(session) {
  if (!session?.user) return false;
  return session.user.backendRole === 'Executive' || session.user.uiRole === 'executive';
}

// Helper function to check if a user has specific role
export function hasRole(session, role) {
  if (!session?.user) return false;
  return session.user.role === role || session.user.backendRole === role || session.user.uiRole === role;
}
